import { FunctionComponent, useState, useCallback, useMemo, useEffect } from 'react';
import styled from "styled-components";
import HeaderLab from './Header';
import { Typography, Layout, Space, Button, theme, Segmented, Input, Popover, message, Col, Row } from 'antd';
import { ExportOutlined, SearchOutlined, PlusOutlined, CalendarOutlined, ImportOutlined, ScanOutlined } from '@ant-design/icons';
import ReagentsTable, { DataType } from '../tables/Reagents';
import StandartSamplesTable, { StandardType } from '../tables/StandartSamples';
import PrecursorsTable, { PrecursorType } from '../tables/Precursors';
import debounce from 'lodash/debounce';
import Screen from './Screen';
import PurchaseRequest from '../../components/PurchaseRequest';
import ExportTypes, { ExportFormat } from '../../components/ExportTypes';
import ExportProcess from '../../components/ExportProcess';
import Scan from '../../components/Scan';
import { useParams, useNavigate } from 'react-router-dom';
import { getReagents, exportReagents, exportStandards, getPrecursorRegistries, PrecursorRegistryData } from '../../api/chemicalRegistry';
import ScreenSS from './ScreenSS';
import { getStandards } from '../../api/chemicalRegistry';
import ImportUploadFile from '../../components/ImportUploadFile';
import ScanPreviewStandardSample from '../../components/ScanPreviewStandardSample';
import MultipleStandardSamplesAdd from '../../components/MultipleStandardSamplesAdd';

const { Title } = Typography;
const { Content } = Layout;
const { useToken } = theme;

const StyledContent = styled(Content)`
    padding: 32px;
    background-color: #f5f5f5;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
`;

const TextParentRoot = styled.div`
    width: 100%;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    text-align: left;
    color: #262626;
    font-family: Roboto;
`;

const ControlsBlock = styled.div`
    // margin-top: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const HeadingExtra = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SearchInput = styled(Input)`
  width: 45px;
  height: 40px;
  border-color: #d9d9d9;
  border-radius: 6px !important;
  transition: all 0.3s ease-in-out;
  padding: 0;
  
  &.expanded {
    width: 240px;
    padding: 4px 11px;
    border-color: #d9d9d9;
  }

  &:hover {
    border-color: #d9d9d9;
  }

  &:focus {
    border-color: #d9d9d9;
    box-shadow: none;
  }

  .ant-input {
    height: 40px;
    padding: 0;
    display: flex;
    align-items: center;
  }

  .ant-input-prefix {
    margin: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  input {
    line-height: normal !important;
    height: 100% !important;
  }

  &:not(.expanded) {
    .ant-input-prefix {
      margin-right: 0;
    }
    
    .ant-input {
      width: 0;
      padding: 0;
    }
  }

  &.expanded .ant-input-prefix {
    width: auto;
    margin-right: 8px;
  }
`;

const Laboratory: FunctionComponent = () => {
    const { token } = useToken();
    const { laboratoryId } = useParams();
    const [reagents, setReagents] = useState<DataType[]>([]);
    const [standards, setStandards] = useState<StandardType[]>([]);
    const [precursorsData, setPrecursorsData] = useState<PrecursorType[]>([]);
    const [loading, setLoading] = useState(true);
    const [resetSelection, setResetSelection] = useState(false);
    const [selectedPrecursorGroup, setSelectedPrecursorGroup] = useState<string>('');
    const [precursorGroupInitialized, setPrecursorGroupInitialized] = useState(false);
    const [selectedRows, setSelectedRows] = useState<{
        reagents: React.Key[];
        standards: React.Key[];
        precursors: React.Key[];
    }>({
        reagents: [],
        standards: [],
        precursors: []
    });

    const [activeSegment, setActiveSegment] = useState<string>(() => {
        const saved = localStorage.getItem('chemicalsActiveSegment');
        return saved || 'Реактивы';
    });

    useEffect(() => {
        localStorage.setItem('chemicalsActiveSegment', activeSegment);
    }, [activeSegment]);

    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    
    const [modalStates, setModalStates] = useState({
        screen: false,
        purchaseRequest: false,
        export: false,
        exportProcess: false,
        scan: false,
        import: false,
        scanPreviewStandard: false,
        multipleAdd: false
    });

    const navigate = useNavigate();

    // Добавляем состояния для исходных данных
    const [originalReagents, setOriginalReagents] = useState<DataType[]>([]);
    const [originalStandards, setOriginalStandards] = useState<StandardType[]>([]);
    const [originalPrecursors, setOriginalPrecursors] = useState<PrecursorType[]>([]);

    // Добавляем состояние для хранения отсканированных данных
    const [scannedData, setScannedData] = useState<string>('');

    const fetchReagents = useCallback(async () => {
        try {
            if (!laboratoryId) return;
            
            console.log('Fetching reagents for laboratory:', laboratoryId);
            const reagentsData = await getReagents(laboratoryId);
            console.log('Fetched reagents:', reagentsData);
            
            // Сортируем данные от нового к старому
            const sortedReagents = [...reagentsData].sort((a, b) => b.number - a.number);
            
            setReagents(sortedReagents);
            setOriginalReagents(reagentsData); // Сохраняем исходные данные без сортировки для фильтрации
        } catch (error) {
            console.error('Error fetching reagents:', error);
            message.error('Ошибка при загрузке реагентов');
        }
    }, [laboratoryId]);

    const fetchStandards = useCallback(async () => {
        try {
            if (!laboratoryId) return;
            
            console.log('Fetching standards for laboratory:', laboratoryId);
            const standardsData = await getStandards(Number(laboratoryId));
            console.log('Fetched standards:', standardsData);
            
            // Сортируем данные от нового к старому
            const sortedStandards = [...standardsData].sort((a, b) => b.number - a.number);
            
            setStandards(sortedStandards);
            setOriginalStandards(standardsData); // Сохраняем исходные данные без сортировки для фильтрации
        } catch (error) {
            console.error('Error fetching standards:', error);
            message.error('Ошибка при загрузке стандартных образцов');
        }
    }, [laboratoryId]);

    const fetchPrecursors = useCallback(async () => {
        try {
            if (!laboratoryId) return;
            
            console.log('🔥 Chemicals: fetchPrecursors called, laboratoryId:', laboratoryId, 'selectedPrecursorGroup:', selectedPrecursorGroup);
            const precursorsData = await getPrecursorRegistries(laboratoryId, selectedPrecursorGroup === '' ? undefined : selectedPrecursorGroup);
            console.log('✅ Chemicals: Fetched precursors:', precursorsData.length, 'items for group:', selectedPrecursorGroup);
            
            // Преобразуем данные из API в формат для таблицы
            const transformedPrecursors: PrecursorType[] = precursorsData.map((item: PrecursorRegistryData) => ({
                key: item.id,
                name: item.name || '',
                batch: item.batch || '',
                manufacturer: item.manufacturer || '',
                category: item.category || '',
                concentration: item.concentration || '',
                location: item.location || '',
                registrationNumber: item.registration_number || '',
                month: item.month || '',
                initialBalance: item.initial_balance || '0',
                income: {
                    operationNumber: item.income_operation_number || '',
                    date: item.income_date || '',
                    document: item.income_document || '',
                    quantity: item.income_quantity || '0',
                    responsible: item.income_responsible || ''
                },
                monthlyIncome: item.monthly_income || '0',
                totalWithBalance: item.total_with_balance || '0',
                expense: {
                    operationNumber: item.expense_operation_number || '',
                    type: item.expense_type || '',
                    date: item.expense_date || '',
                    document: item.expense_document || '',
                    quantity: item.expense_quantity || '0',
                    responsible: item.expense_responsible || ''
                },
                monthlyExpense: item.monthly_expense || '0',
                finalBalance: item.final_balance || '0',
                actualBalance: item.actual_balance || '0'
            }));
            
            setPrecursorsData(transformedPrecursors);
            setOriginalPrecursors(transformedPrecursors);
        } catch (error) {
            console.error('Error fetching precursors:', error);
            message.error('Ошибка при загрузке прекурсоров');
        }
    }, [laboratoryId, selectedPrecursorGroup]);

    useEffect(() => {
        if (!laboratoryId) {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            if (user.laboratory_id) {
                navigate(`/laboratory/${user.laboratory_id}/chemicals`);
            }
        } else {
            setLoading(true);
            // Инициализируем группу прекурсоров как "все группы" сразу
            if (!precursorGroupInitialized) {
                setSelectedPrecursorGroup('');
                setPrecursorGroupInitialized(true);
            }
            Promise.all([fetchReagents(), fetchStandards()])
                .finally(() => setLoading(false));
        }
    }, [laboratoryId, navigate, fetchReagents, fetchStandards]);

    // Отдельный useEffect для загрузки прекурсоров при изменении группы
    useEffect(() => {
        if (laboratoryId) {
            console.log('🚀 Chemicals: useEffect triggered, selectedPrecursorGroup:', selectedPrecursorGroup);
            fetchPrecursors();
        }
    }, [selectedPrecursorGroup, laboratoryId, fetchPrecursors]);

    const handleScreenSuccess = async () => {
        if (activeSegment === 'Реактивы') {
            await fetchReagents();
        } else if (activeSegment === 'Стандартные образцы (СО)') {
            await fetchStandards();
        }
        
        // Всегда обновляем прекурсоры, так как они могут изменяться при добавлении реагентов-прекурсоров
        await fetchPrecursors();
        
        // Применяем текущий фильтр с сортировкой
        if (searchValue) {
            filterData(searchValue);
        }
    };

    const searchInFields = useCallback((item: any, value: string) => {
        const lowerValue = value.toLowerCase();
        
        // Специальная обработка для реагентов
        if (item.receipt) {
            const receiptMatch = Object.values(item.receipt).some(field => 
                String(field).toLowerCase().includes(lowerValue)
            );
            if (receiptMatch) return true;
        }
        
        // Поиск по остальным полям
        const searchableFields = Object.values(item).filter(
            field => typeof field === 'string' || typeof field === 'number'
        );
        
        return searchableFields.some(field => 
            String(field).toLowerCase().includes(lowerValue)
        );
    }, []);

    const filterData = useCallback((value: string) => {
        if (!value) {
            // Возвращаем исходные данные с сортировкой
            setReagents([...originalReagents].sort((a, b) => b.number - a.number));
            setStandards([...originalStandards].sort((a, b) => b.number - a.number));
            setPrecursorsData([...originalPrecursors]); // Для прекурсоров сортировка по дате будет в самой таблице
            return;
        }

        // Фильтруем из исходных данных и сортируем
        const filteredReagents = originalReagents.filter(item => searchInFields(item, value));
        const filteredStandards = originalStandards.filter(item => searchInFields(item, value));
        const filteredPrecursors = originalPrecursors.filter(item => searchInFields(item, value));
        
        setReagents([...filteredReagents].sort((a, b) => b.number - a.number));
        setStandards([...filteredStandards].sort((a, b) => b.number - a.number));
        setPrecursorsData(filteredPrecursors); // Для прекурсоров сортировка по дате будет в самой таблице
    }, [searchInFields, originalReagents, originalStandards, originalPrecursors]);

    const debouncedFilter = useMemo(
        () => debounce((value: string) => {
            filterData(value);
        }, 300),
        [filterData]
    );

    const handleSearch = useCallback((value: string) => {
        setSearchValue(value);
        debouncedFilter(value);
    }, [debouncedFilter]);

    const handleSegmentChange = useCallback((value: string | number) => {
        setActiveSegment(value.toString());
        if (searchValue) {
            filterData(searchValue);
        }
    }, [searchValue]);

    const handleExport = async (format: ExportFormat) => {
        try {
            setModalStates(prev => ({
                ...prev,
                export: false,
                exportProcess: true
            }));
            
            if (activeSegment === 'Реактивы') {
                await exportReagents(selectedRows.reagents, format);
            } else if (activeSegment === 'Стандартные образцы (СО)') {
                await exportStandards(selectedRows.standards, format);
            }
            
            message.success('Экспорт успешно завершен');
        } catch (error) {
            message.error('Ошибка при экспорте');
        } finally {
            setModalStates(prev => ({
                ...prev,
                exportProcess: false
            }));
        }
    };

    const selectedItems = useMemo(() => {
        switch (activeSegment) {
            case 'Реактивы':
                return reagents
                    .filter(item => selectedRows.reagents.includes(item.key))
                    .map((item, index) => ({
                        key: item.key,
                        number: index + 1,
                        name: item.receipt.name
                    }));
            case 'Стандартные образцы (СО)':
                return standards
                    .filter(item => selectedRows.standards.includes(item.key))
                    .map((item, index) => ({
                        key: item.key,
                        number: index + 1,
                        name: item.name
                    }));
            case 'Прекурсоры':
                return precursorsData
                    .filter(item => selectedRows.precursors.includes(item.key))
                    .map((item, index) => ({
                        key: item.key,
                        number: index + 1,
                        name: item.name
                    }));
            default:
                return [];
        }
    }, [selectedRows, reagents, standards, precursorsData, activeSegment]);

    const filteredData = useMemo(() => {
        if (!searchValue) return { reagents, standards, precursors: precursorsData };

        return {
            reagents: reagents.filter(item => searchInFields(item, searchValue)),
            standards: standards.filter(item => searchInFields(item, searchValue)),
            precursors: precursorsData.filter(item => searchInFields(item, searchValue))
        };
    }, [reagents, standards, precursorsData, searchValue, searchInFields]);

    const getCurrentData = useCallback(() => {
        switch (activeSegment) {
            case 'Реактивы':
                return reagents;
            case 'Стандартные образцы (СО)':
                return standards;
            case 'Прекурсоры':
                return precursorsData;
            default:
                return [];
        }
    }, [activeSegment, reagents, standards, precursorsData]);

    const handlePrecursorGroupSelect = useCallback((group: string) => {
        console.log('📋 Chemicals: handling precursor group select:', group, 'current:', selectedPrecursorGroup);
        if (!precursorGroupInitialized) {
            console.log('🎯 Chemicals: first initialization, setting group:', group);
            setSelectedPrecursorGroup(group);
            setPrecursorGroupInitialized(true);
        } else if (group !== selectedPrecursorGroup) {
            console.log('🔄 Chemicals: updating selectedPrecursorGroup to:', group);
            setSelectedPrecursorGroup(group);
        } else {
            console.log('❌ Chemicals: group unchanged, skipping update');
        }
    }, [selectedPrecursorGroup, precursorGroupInitialized]);

    // Добавляем компонент для отображения кнопок вместо пустой таблицы
    const EmptyStateButtons = () => {
        const { token } = useToken();
        
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                width: '100%', 
                height: '400px',
                marginTop: '50px'
            }}>
                <Row gutter={[60, 0]} justify="center" align="middle">
                    <Col>
                        <Button 
                            onClick={() => setModalStates(prev => ({ ...prev, import: true }))}
                            style={{ 
                                width: 240, 
                                height: 226, 
                                display: 'flex', 
                                flexDirection: 'column', 
                                justifyContent: 'center', 
                                alignItems: 'center',
                                backgroundColor: token['blue-5'], 
                                color: 'white',
                                fontWeight: 500,
                                fontSize: 16
                            }}
                        >
                            <ImportOutlined style={{ fontSize: 120, color: 'white', marginBottom: 16, opacity: 0.6 }} />
                            <span>Импортировать данные</span>
                        </Button>
                    </Col>
                    <Col>
                        <Button 
                            onClick={() => setModalStates(prev => ({ ...prev, scan: true }))}
                            style={{ 
                                width: 240, 
                                height: 226, 
                                display: 'flex', 
                                flexDirection: 'column', 
                                justifyContent: 'center', 
                                alignItems: 'center',
                                fontWeight: 500,
                                fontSize: 16
                            }}
                        >
                            <ScanOutlined style={{ fontSize: 120, color: token['blue-5'], marginBottom: 16 }} />
                            <span>Сканировать</span>
                        </Button>
                    </Col>
                    <Col>
                        <Button 
                            onClick={() => setModalStates(prev => ({ ...prev, screen: true }))}
                            style={{ 
                                width: 240, 
                                height: 226, 
                                display: 'flex', 
                                flexDirection: 'column', 
                                justifyContent: 'center', 
                                alignItems: 'center',
                                fontWeight: 500,
                                fontSize: 16
                            }}
                        >
                            <PlusOutlined style={{ fontSize: 120, color: token['blue-5'], marginBottom: 16 }} />
                            <span>Добавить вручную</span>
                        </Button>
                    </Col>
                </Row>
            </div>
        );
    };

    const renderTable = useMemo(() => {
        const currentData = getCurrentData();
        const isDataEmpty = currentData.length === 0;
        
        // Показываем кнопки только если загрузка завершена и данных нет
        if (!loading && isDataEmpty && activeSegment !== 'Прекурсоры') {
            return <EmptyStateButtons />;
        }
        
        // Во время загрузки можно показать индикатор загрузки
        if (loading) {
            return <div style={{ display: 'flex', justifyContent: 'center', marginTop: '100px' }}>
                <div className="ant-spin ant-spin-lg ant-spin-spinning">
                    <span className="ant-spin-dot ant-spin-dot-spin">
                        <i className="ant-spin-dot-item"></i>
                        <i className="ant-spin-dot-item"></i>
                        <i className="ant-spin-dot-item"></i>
                        <i className="ant-spin-dot-item"></i>
                    </span>
                </div>
            </div>;
        }
        
        switch (activeSegment) {
            case 'Реактивы':
                return (
                    <ReagentsTable 
                        data={currentData as DataType[]}
                        onSelectionChange={(keys) => setSelectedRows(prev => ({ ...prev, reagents: keys }))}
                        laboratoryId={laboratoryId!}
                        resetSelection={resetSelection}
                    />
                );
            case 'Стандартные образцы (СО)':
                return (
                    <StandartSamplesTable 
                        data={currentData as StandardType[]}
                        onSelectionChange={(keys) => setSelectedRows(prev => ({ ...prev, standards: keys }))}
                        resetSelection={resetSelection}
                    />
                );
            case 'Прекурсоры':
                return (
                    <PrecursorsTable 
                        data={currentData as PrecursorType[]}
                        onSelectionChange={(keys) => setSelectedRows(prev => ({ ...prev, precursors: keys }))}
                        resetSelection={resetSelection}
                        onGroupSelect={handlePrecursorGroupSelect}
                        selectedGroup={selectedPrecursorGroup}
                    />
                );
            default:
                return null;
        }
    }, [activeSegment, getCurrentData, laboratoryId, resetSelection, loading, handlePrecursorGroupSelect, selectedPrecursorGroup]);

    const shouldShowButtons = useMemo(() => activeSegment !== 'Прекурсоры', [activeSegment]);

    const getScreenComponent = () => {
        if (activeSegment === 'Стандартные образцы (СО)') {
            return (
                <ScreenSS 
                    onCancel={() => setModalStates(prev => ({
                        ...prev,
                        screen: false
                    }))}
                    onSuccess={handleScreenSuccess}
                    onMultipleAdd={() => {
                        setModalStates(prev => ({
                            ...prev,
                            screen: false,
                            multipleAdd: true
                        }));
                    }}
                />
            );
        }
        return (
            <Screen 
                onCancel={() => setModalStates(prev => ({
                    ...prev,
                    screen: false
                }))}
                onSuccess={handleScreenSuccess}
            />
        );
    };

    const handleImport = (file: File, headerRows: number) => {
        console.log('Importing file:', file, 'with header rows:', headerRows);
        message.success('Файл успешно импортирован');
        setModalStates(prev => ({
            ...prev,
            import: false
        }));
    };

    const handleScan = async (scannedData: string) => {
        try {
            console.log('Отсканированные данные:', scannedData);
            
            // Пытаемся распарсить JSON из QR-кода
            try {
                const parsedData = JSON.parse(scannedData);
                
                // Если это QR-код стандартного образца (содержит batch_id)
                if (parsedData.batch_id !== undefined) {
                    console.log('Обнаружен QR-код стандартного образца:', parsedData);
                    setScannedData(scannedData);
                    
                    // Открываем модальное окно предпросмотра стандартного образца
                    setModalStates(prev => ({
                        ...prev,
                        scanPreviewStandard: true
                    }));
                    return;
                }
            } catch (e) {
                // Если не удалось распарсить JSON, продолжаем обычный поиск
                console.log('Не удалось распарсить JSON из QR-кода:', e);
            }
            
            // Обычный поиск реактива по отсканированным данным
            const foundReagent = reagents.find(reagent => 
                reagent.receipt.batch === scannedData || 
                reagent.receipt.name === scannedData
            );

            if (foundReagent) {
                message.success(`Найден реактив: ${foundReagent.receipt.name}`);
                // Здесь можно добавить дополнительную логику, например:
                // - Открыть карточку реактива
                // - Обновить количество
                // - и т.д.
            } else {
                message.warning('Реактив не найден');
            }
        } catch (error) {
            console.error('Ошибка при обработке отсканированных данных:', error);
            message.error('Ошибка при обработке отсканированных данных');
        }
    };

    // Обработчик успешного добавления стандартного образца
    const handleScanSuccess = () => {
        // Обновляем список стандартных образцов
        fetchStandards();
        
        // Переключаемся на вкладку "Стандартные образцы"
        setActiveSegment('Стандартные образцы (СО)');
        
        // Применяем текущий фильтр с сортировкой
        if (searchValue) {
            filterData(searchValue);
        }
        
        message.success('Стандартный образец успешно добавлен');
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <HeaderLab />
            <StyledContent>
                <ControlsBlock style={{ marginBottom: 24 }}>
                    <Segmented 
                        size="large" 
                        options={['Реактивы', 'Стандартные образцы (СО)', 'Прекурсоры']} 
                        style={{ backgroundColor: 'rgba(0, 0, 0, 0.06)' }}
                        value={activeSegment}
                        onChange={handleSegmentChange}
                    />
                    <HeadingExtra>
                        <SearchInput
                            className={isSearchExpanded ? "expanded" : ""}
                            placeholder={isSearchExpanded ? "Поиск" : ""}
                            prefix={<SearchOutlined style={{ color: token['blue-6'] }} />}
                            onFocus={() => setIsSearchExpanded(true)}
                            onChange={(e) => handleSearch(e.target.value)}
                            onBlur={(e) => {
                                if (!e.target.value) {
                                    setIsSearchExpanded(false);
                                }
                            }}
                            style={{ height: '40px' }}
                            value={searchValue}
                        />
                        <Button 
                            icon={<ExportOutlined style={{ color: token['blue-5'] }} />} 
                            style={{ height: '40px', width: '45px' }}
                            onClick={() => setModalStates(prev => ({
                                ...prev,
                                export: true
                            }))}
                        />
                        <Button 
                            icon={<ImportOutlined style={{ color: token['blue-5'] }} />} 
                            style={{ height: '40px' }}
                            onClick={() => setModalStates(prev => ({
                                ...prev,
                                import: true
                            }))}
                        >
                            Импорт
                        </Button>
                        {shouldShowButtons && (
                            <>
                                <Popover 
                                    content={getScreenComponent()}
                                    trigger="click"
                                    open={modalStates.screen}
                                    onOpenChange={(visible) => setModalStates(prev => ({
                                        ...prev,
                                        screen: visible
                                    }))}
                                    placement="bottomLeft"
                                    overlayStyle={{ 
                                        padding: 0,
                                        zIndex: 1100,
                                        marginTop: '57px'
                                    }}
                                    overlayInnerStyle={{ 
                                        padding: 0
                                    }}
                                    arrow={false}
                                >
                                    <Button 
                                        icon={<PlusOutlined style={{ color: token['blue-5'] }} />} 
                                        style={{ height: '40px' }}
                                    >
                                        Добавить запись
                                    </Button>
                                </Popover>
                                <Button 
                                    icon={<ScanOutlined style={{ color: token['blue-5'] }} />} 
                                    style={{ height: '40px' }}
                                    onClick={() => setModalStates(prev => ({
                                        ...prev,
                                        scan: true
                                    }))}
                                >
                                    Скан
                                </Button>
                                <Button 
                                    icon={
                                        (activeSegment === 'Реактивы' && selectedRows.reagents.length > 0) || 
                                        (activeSegment === 'Стандартные образцы (СО)' && selectedRows.standards.length > 0) ? 
                                            <span style={{ 
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 16,
                                                height: 16,
                                                backgroundColor: token['blue-5'],
                                                borderRadius: '50%',
                                                color: 'white',
                                                fontSize: 12,
                                                marginRight: 0
                                            }}>
                                                {activeSegment === 'Реактивы' 
                                                    ? selectedRows.reagents.length 
                                                    : selectedRows.standards.length}
                                            </span> : 
                                            <PlusOutlined style={{ color: token['blue-5'] }} />
                                    }
                                    style={{ height: '40px' }}
                                    onClick={() => setModalStates(prev => ({
                                        ...prev,
                                        purchaseRequest: true
                                    }))}
                                >
                                    Заявка на закупку
                                </Button>
                            </>
                        )}
                    </HeadingExtra>
                </ControlsBlock>
                {renderTable}
                <PurchaseRequest 
                    open={modalStates.purchaseRequest}
                    onCancel={() => setModalStates(prev => ({
                        ...prev,
                        purchaseRequest: false
                    }))}
                    selectedReagents={selectedItems}
                    onSuccess={() => {
                        setSelectedRows({
                            reagents: [],
                            standards: [],
                            precursors: []
                        });
                        setResetSelection(prev => !prev);
                    }}
                />
                <ExportTypes 
                    open={modalStates.export}
                    onCancel={() => setModalStates(prev => ({
                        ...prev,
                        export: false
                    }))}
                    onExport={handleExport}
                    selectedRows={
                        activeSegment === 'Реактивы' 
                            ? selectedRows.reagents 
                            : selectedRows.standards
                    }
                />
                <ExportProcess 
                    open={modalStates.exportProcess}
                />
                <Scan 
                    open={modalStates.scan}
                    onCancel={() => setModalStates(prev => ({
                        ...prev,
                        scan: false
                    }))}
                    onScan={handleScan}
                />
                <ImportUploadFile 
                    open={modalStates.import}
                    onCancel={() => setModalStates(prev => ({
                        ...prev,
                        import: false
                    }))}
                    onImport={handleImport}
                    tableType={activeSegment === 'Реактивы' ? 'reagents' : 'standartSamples'}
                />
                <ScanPreviewStandardSample
                    open={modalStates.scanPreviewStandard}
                    onCancel={() => setModalStates(prev => ({ ...prev, scanPreviewStandard: false }))}
                    onSuccess={handleScanSuccess}
                    scannedData={scannedData}
                />
                <MultipleStandardSamplesAdd
                    open={modalStates.multipleAdd}
                    onCancel={() => setModalStates(prev => ({ ...prev, multipleAdd: false }))}
                    onSuccess={handleScreenSuccess}
                />
            </StyledContent>
        </Layout>
    );
};

export default Laboratory; 